import { useState, useEffect, useRef, useMemo } from "react";
import "./Trade.css";
import { useModalContext } from "../../../Context/ModalContext.js";
import { useSelector } from "react-redux";

export const OrderBook = ({
  asks,
  bids,
  coin,
  primaryCoin,
  latestTradeType,
  latestTradePrice,
  averageOfLatestPrices,
}) => {
  const { selectedNumberFormat, selectedFiatEstimation } = useModalContext();
  const labels = useSelector((state) => state?.labels?.data);
  const exchangeCommonSettings = useSelector(
    (state) => state?.exchange?.exchangeCommonSettings
  );

  const tbodyRef = useRef(null);

  const getPrecision = (minSize) => {
    if (!minSize || minSize >= 1) return 0;

    let precision = 0;
    let size = minSize;

    while (size < 1.0) {
      size *= 10.0;
      precision++;
    }
    return precision;
  };

  const pairSettings = useMemo(() => {
    if (!exchangeCommonSettings?.data?.trade_setting || !coin) {
      return {
        pricePrecision: 2,
        quantityPrecision: 6,
      };
    }

    const setting = exchangeCommonSettings.data.trade_setting.find(
      (item) => item.coinName === coin.base && item.marketName === coin.quote
    );

    if (!setting) {
      return {
        pricePrecision: 2,
        quantityPrecision: 6,
      };
    }

    const calculatedPricePrecision = getPrecision(setting.minTickSize);
    const calculatedQuantityPrecision = getPrecision(setting.minTradeAmount);

    // REMOVED: Math.max(calculatedPricePrecision, 2)
    // Now uses the actual calculated precision
    const pricePrecision = calculatedPricePrecision;

    return {
      pricePrecision,
      quantityPrecision: calculatedQuantityPrecision,
    };
  }, [exchangeCommonSettings, coin]);

  const formatWithPrecision = (value, precision) => {
    if (value === null || value === undefined || isNaN(value)) {
      if (selectedNumberFormat?.short === "comma") {
        return precision > 0 ? "0," + "0".repeat(precision) : "0";
      }
      return precision > 0 ? "0." + "0".repeat(precision) : "0";
    }

    let numValue;
    if (typeof value === "string") {
      const lastComma = value.lastIndexOf(",");
      const lastDot = value.lastIndexOf(".");

      if (lastComma > lastDot) {
        numValue = parseFloat(value.replace(/\./g, "").replace(",", "."));
      } else {
        numValue = parseFloat(value.replace(/,/g, ""));
      }
    } else {
      numValue = parseFloat(value);
    }

    if (isNaN(numValue)) {
      if (selectedNumberFormat?.short === "comma") {
        return precision > 0 ? "0," + "0".repeat(precision) : "0";
      }
      return precision > 0 ? "0." + "0".repeat(precision) : "0";
    }

    const fixedValue = numValue.toFixed(precision);

    const parts = fixedValue.split(".");
    const integerPart = parts[0];
    const decimalPart = parts[1] || "";

    const withThousands = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (selectedNumberFormat?.short === "comma") {
      const formatted = withThousands.replace(/,/g, ".");
      return decimalPart ? `${formatted},${decimalPart}` : formatted;
    } else {
      return decimalPart ? `${withThousands}.${decimalPart}` : withThousands;
    }
  };

  function getFiatEstimation() {
    if (selectedFiatEstimation?.name === "TRY") {
      return "₺";
    } else if (selectedFiatEstimation?.name === "USD") {
      return "$";
    } else {
      return selectedFiatEstimation?.name || "";
    }
  }

  const [isUserScrolling, setIsUserScrolling] = useState(false);
  let scrollTimeout = null;

  useEffect(() => {
    const handleScroll = () => {
      if (tbodyRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = tbodyRef.current;
        setIsUserScrolling(scrollTop + clientHeight < scrollHeight - 10);

        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => setIsUserScrolling(false), 3000);
      }
    };

    const tbodyElement = tbodyRef.current;
    if (tbodyElement) {
      tbodyElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (tbodyElement) {
        tbodyElement.removeEventListener("scroll", handleScroll);
      }
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isUserScrolling && tbodyRef.current) {
      tbodyRef.current.scrollTop = tbodyRef.current.scrollHeight;
    }
  }, [asks, isUserScrolling]);

  return (
    <div className="order-book">
      <div className="d-flex justify-content-between flex-col flex-lg-row flex-md-row px-2">
        <div className="d-flex justify-content-between w-100 align-items-center mb-1">
          <h2 className="order-book-title fs-5 mb-0 me-2">
            {labels?.exchange?.orderBook}
          </h2>
          <div className="d-flex gap-1">
            <div className="d-flex flex-column gap-1">
              <span className="book-indicator bg-success"></span>
              <span className="book-indicator bg-danger"></span>
            </div>
            <div className="d-flex flex-column gap-1">
              <span className="book-indicator bg-success"></span>
              <span className="book-indicator bg-danger"></span>
            </div>
            <div className="d-flex flex-column gap-1">
              <span className="book-indicator bg-success"></span>
              <span className="book-indicator bg-danger"></span>
            </div>
          </div>
        </div>
      </div>

      {/* ASKS - Sell Orders */}
      <div className="table-responsive table-fixed-width scrollbar-custom px-2 table-sell-orders">
        <table className="table table-sm table-order-book table-no-border mb-1">
          <thead>
            <tr className="small">
              <th className="text-muted px-0 small text-end pt-2 pb-2">
                {labels?.exchange?.price} [
                {coin?.quote || primaryCoin?.quote || "..."}]
              </th>
              <th className="text-muted px-0 small text-end pt-2 pb-2">
                {labels?.exchange?.size} [
                {coin?.base || primaryCoin?.base || "..."}]
              </th>
              <th className="text-muted px-0 small text-end pt-2 pb-2">
                {labels?.exchange?.total} [
                {coin?.quote || primaryCoin?.quote || "..."}]
              </th>
            </tr>
          </thead>
          <tbody className="asks-scrollable-content" ref={tbodyRef}>
            {asks?.map((ask, index) => {
              const price = Array.isArray(ask) ? ask[0] : ask.price;
              const size = Array.isArray(ask) ? ask[1] : ask.size;

              const priceNum =
                typeof price === "string"
                  ? parseFloat(price.replace(/,/g, "."))
                  : parseFloat(price);
              const sizeNum =
                typeof size === "string"
                  ? parseFloat(size.replace(/,/g, "."))
                  : parseFloat(size);

              const total = priceNum * sizeNum;

              return (
                <tr key={index} className="border-0 small">
                  <td className="text-danger border-0 text-end">
                    {formatWithPrecision(priceNum, pairSettings.pricePrecision)}
                  </td>
                  <td className="border-0 text-end">
                    {formatWithPrecision(
                      sizeNum,
                      pairSettings.quantityPrecision
                    )}
                  </td>
                  <td className="border-0 text-end">
                    {formatWithPrecision(total, pairSettings.pricePrecision)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Latest Price */}
      <div className="table-responsive table-fixed-width">
        <table className="table table-sm table-order-book table-no-border mb-1">
          <tbody className="border-top border-bottom custom-border-color border-secondary table-border pb-0">
            <tr>
              <td
                className={
                  latestTradeType === "BUY"
                    ? "text-success text-end pe-2"
                    : "text-danger text-end pe-2"
                }
              >
                {formatWithPrecision(
                  latestTradePrice,
                  pairSettings.pricePrecision
                )}
              </td>
              <td>
                {getFiatEstimation()}
                &nbsp;
                {isNaN(averageOfLatestPrices)
                  ? "0"
                  : formatWithPrecision(
                      averageOfLatestPrices,
                      pairSettings.pricePrecision
                    )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* BIDS - Buy Orders */}
      <div className="table-responsive table-fixed-width px-2">
        <table className="table table-sm table-order-book table-no-border mb-1 table-buy-orders">
          <tbody>
            {bids?.map((bid, index) => {
              const price = Array.isArray(bid) ? bid[0] : bid.price;
              const size = Array.isArray(bid) ? bid[1] : bid.size;

              const priceNum =
                typeof price === "string"
                  ? parseFloat(price.replace(/,/g, "."))
                  : parseFloat(price);
              const sizeNum =
                typeof size === "string"
                  ? parseFloat(size.replace(/,/g, "."))
                  : parseFloat(size);

              const total = priceNum * sizeNum;

              return (
                <tr key={index} className="border-0 small">
                  <td className="text-success border-0 text-end">
                    {formatWithPrecision(priceNum, pairSettings.pricePrecision)}
                  </td>
                  <td className="border-0 text-end">
                    {formatWithPrecision(
                      sizeNum,
                      pairSettings.quantityPrecision
                    )}
                  </td>
                  <td className="border-0 text-end">
                    {formatWithPrecision(total, pairSettings.pricePrecision)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
